import { useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BrandLogo } from "@/components/shared/BrandLogo";
import {
  Plus, X, Clock, ChevronLeft, ChevronRight, Minus, Search,
  DollarSign, Car, GripVertical,
} from "lucide-react";
import SchedulerHelpSheet from "./SchedulerHelpSheet";

const HOURS = [];
for (let h = 8; h <= 21; h++) {
  const label = h === 12 ? "12pm" : h > 12 ? `${h - 12}pm` : `${h}am`;
  HOURS.push({ hour: h, label });
}

const EST_DURATION = {
  "Coffee": 10, "Coffee/Cafe": 10, "Boba/Tea": 10,
  "Bakery": 10, "Bakery/Cafe": 10,
  "Fast Food": 15, "Fast Casual": 15,
  "Restaurant": 30, "Restaurant/Buffet": 30,
  "Dessert": 12, "Dessert/Ice Cream": 12,
  "Retail": 20, "Clothing": 20, "Cosmetics": 25,
  "Entertainment": 45, "Service": 20,
};

const CAT_COLOR = {
  "Coffee": "border-l-amber-400 bg-amber-50", "Coffee/Cafe": "border-l-amber-400 bg-amber-50",
  "Fast Food": "border-l-red-400 bg-red-50", "Fast Casual": "border-l-red-400 bg-red-50",
  "Restaurant": "border-l-orange-400 bg-orange-50", "Restaurant/Buffet": "border-l-orange-400 bg-orange-50",
  "Dessert": "border-l-pink-400 bg-pink-50", "Dessert/Ice Cream": "border-l-pink-400 bg-pink-50",
  "Retail": "border-l-violet-400 bg-violet-50", "Clothing": "border-l-indigo-400 bg-indigo-50",
  "Bakery": "border-l-rose-400 bg-rose-50", "Bakery/Cafe": "border-l-rose-400 bg-rose-50",
  "Entertainment": "border-l-sky-400 bg-sky-50", "Cosmetics": "border-l-fuchsia-400 bg-fuchsia-50",
};

export default function TimelineScheduler({ itinerary, onUpdate, themeColor }) {
  const stops = itinerary?.stops || [];
  const numDays = itinerary?.num_days || 1;
  const [activeDay, setActiveDay] = useState(1);

  // Schedule: { [hour]: stop_with_duration }
  const [schedule, setSchedule] = useState(() => {
    const map = {};
    let currentHour = 9; // default start at 9am
    stops.filter(s => (s.day || 1) === 1).forEach(s => {
      if (currentHour > 21) return;
      map[currentHour] = { ...s, duration: EST_DURATION[s.category] || 15 };
      currentHour += Math.ceil((EST_DURATION[s.category] || 15) / 60) + 1;
      if (currentHour - Math.floor(currentHour) === 0) currentHour = Math.ceil(currentHour);
    });
    return map;
  });

  const [showPicker, setShowPicker] = useState(null); // hour number
  const [searchQ, setSearchQ] = useState("");

  const availableStops = useMemo(() => {
    const scheduled = new Set(Object.values(schedule).map(s => s.business_id));
    return stops.filter(s => !scheduled.has(s.business_id));
  }, [schedule, stops]);

  const filteredStops = useMemo(() => {
    if (!searchQ) return availableStops;
    const q = searchQ.toLowerCase();
    return availableStops.filter(s => s.name.toLowerCase().includes(q) || (s.category || "").toLowerCase().includes(q));
  }, [availableStops, searchQ]);

  const addToHour = useCallback((hour, stop) => {
    setSchedule(prev => ({
      ...prev,
      [hour]: { ...stop, duration: EST_DURATION[stop.category] || 15 },
    }));
    setShowPicker(null);
    setSearchQ("");
  }, []);

  const removeFromHour = useCallback((hour) => {
    setSchedule(prev => {
      const next = { ...prev };
      delete next[hour];
      return next;
    });
  }, []);

  const adjustDuration = useCallback((hour, delta) => {
    setSchedule(prev => {
      if (!prev[hour]) return prev;
      const newDur = Math.max(5, Math.min(120, prev[hour].duration + delta));
      return { ...prev, [hour]: { ...prev[hour], duration: newDur } };
    });
  }, []);

  const scheduledCount = Object.keys(schedule).length;
  const totalValue = Object.values(schedule).reduce((a, s) => a + (s.value_est || 0), 0);
  const totalDuration = Object.values(schedule).reduce((a, s) => a + (s.duration || 15), 0);

  return (
    <div className="space-y-4" data-testid="timeline-scheduler">
      {/* Day selector */}
      {numDays > 1 && (
        <div className="flex items-center justify-center gap-3 mb-2">
          <button onClick={() => setActiveDay(d => Math.max(1, d - 1))} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-bold text-slate-700">Day {activeDay} of {numDays}</span>
          <button onClick={() => setActiveDay(d => Math.min(numDays, d + 1))} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}

      {/* Summary */}
      <div className="flex items-center justify-between bg-white rounded-xl p-3 border border-slate-100 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-lg font-black text-slate-800">{scheduledCount}</p>
            <p className="text-[10px] text-slate-400">Scheduled</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-black text-green-600">${totalValue}</p>
            <p className="text-[10px] text-slate-400">Value</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-black text-sky-600">{Math.floor(totalDuration / 60)}h {totalDuration % 60}m</p>
            <p className="text-[10px] text-slate-400">Duration</p>
          </div>
        </div>
        <span className="text-xs text-slate-400">{availableStops.length} left</span>
        <SchedulerHelpSheet type="timeline" />
      </div>

      {/* Vertical Timeline */}
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-[22px] top-0 bottom-0 w-0.5 bg-slate-200" />

        {HOURS.map(({ hour, label }) => {
          const scheduled = schedule[hour];
          const catColor = scheduled ? (CAT_COLOR[scheduled.category] || "border-l-slate-300 bg-slate-50") : "";

          return (
            <div key={hour} className="relative flex items-start gap-3 mb-1" data-testid={`timeline-hour-${hour}`}>
              {/* Hour marker */}
              <div className="flex-shrink-0 w-[44px] text-right pr-2 pt-2">
                <span className={`text-[11px] font-bold ${scheduled ? "text-slate-700" : "text-slate-300"}`}>{label}</span>
              </div>

              {/* Dot on timeline */}
              <div className={`absolute left-[19px] top-3 w-2 h-2 rounded-full ${scheduled ? "bg-sky-500" : "bg-slate-200"} z-10`} />

              {/* Content */}
              <div className="flex-1 min-w-0 pb-1">
                {scheduled ? (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`rounded-xl p-3 border-l-4 ${catColor} shadow-sm`}
                  >
                    <div className="flex items-center gap-2">
                      <BrandLogo name={scheduled.name} logoUrl={scheduled.logo_url} size="xs" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-slate-800 truncate">{scheduled.name}</p>
                        <p className="text-[10px] text-slate-400">{scheduled.category} · ${scheduled.value_est || 0}</p>
                      </div>
                      <button onClick={() => removeFromHour(hour)} className="w-5 h-5 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center">
                        <X className="w-3 h-3 text-red-400" />
                      </button>
                    </div>
                    {/* Duration adjuster */}
                    <div className="flex items-center gap-2 mt-2">
                      <Clock className="w-3 h-3 text-slate-400" />
                      <button onClick={() => adjustDuration(hour, -5)} className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center hover:bg-slate-200"><Minus className="w-3 h-3" /></button>
                      <span className="text-[11px] font-bold text-slate-600 w-10 text-center">{scheduled.duration}m</span>
                      <button onClick={() => adjustDuration(hour, 5)} className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center hover:bg-slate-200"><Plus className="w-3 h-3" /></button>
                    </div>
                  </motion.div>
                ) : (
                  <>
                    {showPicker === hour ? (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="bg-white rounded-xl border border-sky-200 shadow-lg p-3">
                        <div className="relative mb-2">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300" />
                          <input
                            type="text"
                            value={searchQ}
                            onChange={e => setSearchQ(e.target.value)}
                            className="w-full pl-7 pr-3 py-1.5 text-xs bg-slate-50 rounded-lg outline-none"
                            placeholder="Search stops..."
                            autoFocus
                          />
                        </div>
                        <div className="max-h-36 overflow-y-auto space-y-1">
                          {filteredStops.length === 0 && <p className="text-[10px] text-slate-300 text-center py-2">No stops available</p>}
                          {filteredStops.slice(0, 8).map(s => (
                            <button key={s.business_id} onClick={() => addToHour(hour, s)} className="w-full flex items-center gap-2 p-1.5 rounded-lg hover:bg-sky-50 transition text-left">
                              <BrandLogo name={s.name} logoUrl={s.logo_url} size="xs" />
                              <span className="text-[11px] font-semibold text-slate-700 truncate flex-1">{s.name}</span>
                              <span className="text-[10px] text-green-600 font-bold">${s.value_est || 0}</span>
                            </button>
                          ))}
                        </div>
                        <button onClick={() => { setShowPicker(null); setSearchQ(""); }} className="w-full text-center text-[10px] text-slate-400 mt-2 hover:text-slate-600">Cancel</button>
                      </motion.div>
                    ) : (
                      <button
                        onClick={() => setShowPicker(hour)}
                        className="w-full py-2 border border-dashed border-slate-200 rounded-xl text-slate-300 hover:border-sky-300 hover:text-sky-400 hover:bg-sky-50/50 transition flex items-center justify-center gap-1.5"
                      >
                        <Plus className="w-3 h-3" />
                        <span className="text-[10px] font-medium">Add stop</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
